<template>
  <el-dialog
    title="修改订单状态"
    :visible.sync="visible"
    width="600px"
    @close="handleClose"
  >
    <div v-loading="loading">
      <el-form
        ref="form"
        :model="formData"
        :rules="rules"
        label-width="120px"
      >
        <el-form-item label="订单号：">
          <span>{{ orderInfo.sn || '-' }}</span>
        </el-form-item>
        
        <el-form-item label="停运标记" prop="stop_bus">
          <el-radio-group v-model="formData.stop_bus">
            <el-radio :label="0">未停运</el-radio>
            <el-radio :label="1">已停运</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="订单状态" prop="status">
          <el-radio-group v-model="formData.status" @change="handleStatusChange">
            <el-radio :label="0">未支付</el-radio>
            <el-radio :label="1">待出票</el-radio>
            <el-radio :label="2">已出票</el-radio>
            <el-radio :label="3">已完成</el-radio>
            <el-radio :label="4">已取消</el-radio>
            <el-radio :label="5">已退款</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 当订单状态为已退款时显示 -->
        <template v-if="formData.status === 5">
          <el-form-item label="退款状态" prop="back_status">
            <el-radio-group v-model="formData.back_status" @change="handleBackStatusChange">
              <el-radio :label="0">未退款</el-radio>
              <el-radio :label="1">申请退款</el-radio>
              <el-radio :label="2">后台退款</el-radio>
              <el-radio :label="3">已退款</el-radio>
              <el-radio :label="4">已拒绝</el-radio>
            </el-radio-group>
          </el-form-item>

          <!-- 当退款状态为后台退款时显示 -->
          <template v-if="formData.back_status === 2">
            <el-form-item label="退款原因" prop="back_type">
              <el-radio-group v-model="formData.back_type">
                <el-radio :label="0">正常退款</el-radio>
                <el-radio :label="1">停运取消</el-radio>
              </el-radio-group>
            </el-form-item>

            <el-form-item label="退款说明" prop="back_note">
              <el-input
                v-model="formData.back_note"
                type="textarea"
                :rows="3"
                placeholder="请输入退款说明"
              />
            </el-form-item>
          </template>
        </template>
      </el-form>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="submitting">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import axios from 'axios'

export default {
  name: 'OrderStatusDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    orderId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      submitting: false,
      orderInfo: {},
      formData: {
        stop_bus: 0,
        status: 0,
        back_status: 0,
        back_type: 0,
        back_note: ''
      },
      rules: {
        stop_bus: [
          { required: true, message: '请选择停运标记', trigger: 'change' }
        ],
        status: [
          { required: true, message: '请选择订单状态', trigger: 'change' }
        ]
      }
    }
  },
  watch: {
    visible(val) {
      if (val && this.orderId) {
        this.getOrderData()
      }
    }
  },
  methods: {
    async getOrderData() {
      if (!this.orderId) return
      
      this.loading = true
      try {
        const response = await axios.get(`${this.base_url}/orders/getOrderOne/${this.orderId}`) 
        if (response.data.code === 1) {
          this.orderInfo = response.data.data
          // 初始化表单数据
          this.formData = {
            id: this.orderInfo.id,
            stop_bus: this.orderInfo.stop_bus || 0,
            status: this.orderInfo.status || 0,
            back_status: this.orderInfo.back_status || 0,
            back_type: this.orderInfo.back_type || 0,
            back_note: this.orderInfo.back_note || ''
          }
        } else {
          this.$message.error(response.data.message || '获取订单信息失败')
        }
      } catch (error) {
        console.error('获取订单信息失败:', error)
        this.$message.error('获取订单信息失败')
      } finally {
        this.loading = false
      }
    },

    handleStatusChange(value) {
      // 当订单状态不是已退款时，重置退款相关字段
      if (value !== 5) {
        this.formData.back_status = 0
        this.formData.back_type = 0
        this.formData.back_note = ''
      }
    },

    handleBackStatusChange(value) {
      // 当退款状态不是后台退款时，重置退款原因和说明
      if (value !== 2) {
        this.formData.back_type = 0
        this.formData.back_note = ''
      }
    },

    async handleSubmit() {
      try {
        await this.$refs.form.validate()
        
        this.submitting = true
        const submitData = {
          id: this.orderId,
          ...this.formData
        }
        
        // 如果订单状态不是已退款，不提交退款相关字段
        if (this.formData.status !== 5) {
          delete submitData.back_status
          delete submitData.back_type
          delete submitData.back_note
        } else if (this.formData.back_status !== 2) {
          // 如果退款状态不是后台退款，不提交退款原因和说明
          delete submitData.back_type
          delete submitData.back_note
        }

        const response = await axios.post(`${this.base_url}/orders/change_st`, submitData)
        
        if (response.data.code === 1) {
          this.$message.success('订单状态修改成功')
          this.$emit('success')
          this.handleClose()
        } else {
          this.$message.error(response.data.message || '修改失败')
        }
      } catch (error) {
        if (error.message !== 'validation failed') {
          console.error('修改订单状态失败:', error)
          this.$message.error('修改失败')
        }
      } finally {
        this.submitting = false
      }
    },

    handleClose() {
      this.$emit('update:visible', false)
      this.$refs.form && this.$refs.form.resetFields()
      this.orderInfo = {}
      this.formData = {
        stop_bus: 0,
        status: 0,
        back_status: 0,
        back_type: 0,
        back_note: ''
      }
    }
  }
}
</script>

<style scoped>
.dialog-footer {
  text-align: right;
}

.el-form-item {
  margin-bottom: 20px;
}

.el-radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.el-radio {
  margin-right: 0;
}
</style>